import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { UserAvatar, EfsaneBadge, VerifiedBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Trash2, Save, Check, X, Send } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Admin() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [apps, setApps] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [notif, setNotif] = useState({ title: "", body: "", link: "" });
  const logoRef = useRef();
  const productImgRef = useRef();

  const load = async () => {
    const [s, u, r, a, w] = await Promise.all([
      api.get("/admin/settings"), api.get("/admin/users"), api.get("/admin/rooms"),
      api.get("/admin/broadcaster/applications"), api.get("/admin/withdrawals"),
    ]);
    setSettings(s.data); setUsers(u.data); setRooms(r.data); setApps(a.data); setWithdrawals(w.data);
  };
  useEffect(() => { load(); }, []);

  const approveApp = async (id) => {
    try { await api.post(`/admin/broadcaster/applications/${id}/approve`); setApps((a) => a.map((x) => x.id === id ? { ...x, status: "approved" } : x)); toast.success("Yayıncı onaylandı"); } catch { toast.error("Hata"); }
  };
  const rejectApp = async (id) => {
    try { await api.post(`/admin/broadcaster/applications/${id}/reject`); setApps((a) => a.map((x) => x.id === id ? { ...x, status: "rejected" } : x)); toast.success("Başvuru reddedildi"); } catch { toast.error("Hata"); }
  };
  const approveWithdraw = async (id) => {
    try { await api.post(`/admin/withdrawals/${id}/approve`); setWithdrawals((w) => w.map((x) => x.id === id ? { ...x, status: "paid" } : x)); toast.success("Çekim ödendi olarak işaretlendi"); } catch { toast.error("Hata"); }
  };
  const rejectWithdraw = async (id) => {
    try { await api.post(`/admin/withdrawals/${id}/reject`); setWithdrawals((w) => w.map((x) => x.id === id ? { ...x, status: "rejected" } : x)); toast.success("Çekim reddedildi, bakiye iade edildi"); } catch { toast.error("Hata"); }
  };
  const sendBroadcast = async () => {
    if (!notif.title.trim()) { toast.error("Başlık girin"); return; }
    try { const { data } = await api.post("/admin/notifications/broadcast", notif); toast.success(`${data.sent} kullanıcıya gönderildi`); setNotif({ title: "", body: "", link: "" }); } catch { toast.error("Hata"); }
  };

  const grantPremium = async (id) => {
    try { const { data } = await api.post(`/admin/users/${id}/premium?months=1`); setUsers((us) => us.map((x) => x.id === id ? data : x)); toast.success("Premium verildi"); } catch { toast.error("Hata"); }
  };
  const revokePremium = async (id) => {
    try { await api.delete(`/admin/users/${id}/premium`); setUsers((us) => us.map((x) => x.id === id ? { ...x, is_premium: false, premium_until: null } : x)); toast.success("Premium kaldırıldı"); } catch { toast.error("Hata"); }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, logo_url: data.avatar_url }));
      toast.success("Logo yüklendi, kaydetmeyi unutma");
    } catch { toast.error("Yükleme başarısız"); }
  };

  const uploadProductImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, shopier_product_image: data.url }));
      toast.success("Ürün görseli yüklendi, kaydetmeyi unutma");
    } catch { toast.error("Yükleme başarısız"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Kullanıcı silinsin mi?")) return;
    try { await api.delete(`/admin/users/${id}`); setUsers((u) => u.filter((x) => x.id !== id)); toast.success("Silindi"); } catch {}
  };

  const deleteRoom = async (slug) => {
    if (!window.confirm("Oda silinsin mi?")) return;
    try { await api.delete(`/rooms/${slug}`); setRooms((r) => r.filter((x) => x.slug !== slug)); toast.success("Silindi"); } catch {}
  };

  if (!settings) return <div className="min-h-screen bg-[#141414]"><Navbar /></div>;
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-[#141414]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-black text-white mb-6">Yönetim Paneli</h1>
        <Tabs defaultValue="settings">
          <TabsList className="bg-[#181818] border border-white/10 w-full sm:w-auto flex overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="whitespace-nowrap">Site Ayarları</TabsTrigger>
            <TabsTrigger value="banner" data-testid="admin-tab-banner" className="whitespace-nowrap">Banner Reklam</TabsTrigger>
            <TabsTrigger value="premium" data-testid="admin-tab-premium" className="whitespace-nowrap">Premium</TabsTrigger>
            <TabsTrigger value="broadcasters" data-testid="admin-tab-broadcasters" className="whitespace-nowrap">Yayıncılar ({apps.filter((a) => a.status === "pending").length})</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="whitespace-nowrap">Bildirim</TabsTrigger>
            <TabsTrigger value="gifts" data-testid="admin-tab-ecoin" className="whitespace-nowrap">eCoin & Çekim</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="whitespace-nowrap">Kullanıcılar ({users.length})</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="admin-tab-rooms" className="whitespace-nowrap">Odalar ({rooms.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 space-y-5 mt-4">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Site Adı</label>
                <input data-testid="admin-site-name" value={settings.site_name || ""} onChange={(e) => set("site_name", e.target.value)}
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Site Açıklaması</label>
                <textarea data-testid="admin-site-desc" value={settings.site_description || ""} onChange={(e) => set("site_description", e.target.value)} rows={2}
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914] resize-none" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Logo</label>
                <div className="flex items-center gap-4">
                  <img src={mediaUrl(settings.logo_url) || "/logo.png"} alt="logo" className="h-10 bg-white/5 rounded p-1" />
                  <button data-testid="admin-logo-upload" onClick={() => logoRef.current?.click()} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md text-sm">Logo Yükle</button>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                </div>
              </div>
              <button data-testid="admin-save-settings" onClick={saveSettings} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Save size={16} /> Kaydet
              </button>
            </div>
          </TabsContent>

          <TabsContent value="banner">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 space-y-5 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Banner Aktif</div>
                  <div className="text-xs text-gray-500">Ana sayfanın üstünde reklam bandını göster/gizle</div>
                </div>
                <Switch data-testid="admin-banner-toggle" checked={!!settings.banner_active} onCheckedChange={(v) => set("banner_active", v)} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Banner Metni</label>
                <input data-testid="admin-banner-text" value={settings.banner_text || ""} onChange={(e) => set("banner_text", e.target.value)} placeholder="Örn: %50 indirim! Hemen katıl"
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Banner Linki</label>
                <input data-testid="admin-banner-link" value={settings.banner_link || ""} onChange={(e) => set("banner_link", e.target.value)} placeholder="https://..."
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <button data-testid="admin-save-banner" onClick={saveSettings} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Save size={16} /> Kaydet
              </button>
            </div>
          </TabsContent>

          <TabsContent value="premium">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 space-y-5 mt-4">
              <div className="flex items-center justify-between">
                <div><div className="text-white font-medium">Premium (Efsane) Özelliği</div><div className="text-xs text-gray-500">Sitede premium sistemini aç/kapat</div></div>
                <Switch data-testid="admin-premium-toggle" checked={!!settings.premium_enabled} onCheckedChange={(v) => set("premium_enabled", v)} />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Aylık Fiyat (₺)</label>
                <input data-testid="admin-premium-price" value={settings.premium_price || ""} onChange={(e) => set("premium_price", e.target.value)}
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <div className="pt-2 border-t border-white/10">
                <div className="text-white font-medium mb-1">Shopier Ödeme</div>
                <p className="text-xs text-gray-500 mb-3">Shopier → Hesabım → Personal Access Token'dan alın (products & orders yetkileri gerekli).</p>
                <label className="text-sm text-gray-300 mb-1.5 block">Shopier PAT (Bearer Token)</label>
                <textarea data-testid="admin-shopier-pat" rows={3} value={settings.shopier_pat || ""} onChange={(e) => set("shopier_pat", e.target.value)} placeholder="eyJ0eXAiOiJKV1Qi..."
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 mb-3 focus:outline-none focus:border-[#E50914] text-xs font-mono resize-none" />
                <label className="text-sm text-gray-300 mb-1.5 block">Ürün Adı</label>
                <input data-testid="admin-shopier-product-name" value={settings.shopier_product_name || ""} onChange={(e) => set("shopier_product_name", e.target.value)}
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 mb-3 focus:outline-none focus:border-[#E50914]" />
                <label className="text-sm text-gray-300 mb-1.5 block">Ürün Görseli</label>
                <div className="flex items-center gap-4">
                  {settings.shopier_product_image ? <img src={mediaUrl(settings.shopier_product_image)} alt="ürün" className="h-16 w-16 object-cover rounded-lg bg-white/5" /> : <div className="h-16 w-16 rounded-lg bg-white/5 flex items-center justify-center text-gray-600 text-xs">Görsel</div>}
                  <button data-testid="admin-shopier-image-upload" onClick={() => productImgRef.current?.click()} className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md text-sm">Görsel Yükle</button>
                  <input ref={productImgRef} type="file" accept="image/*" className="hidden" onChange={uploadProductImage} />
                </div>
              </div>
              <button data-testid="admin-save-premium" onClick={saveSettings} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Save size={16} /> Kaydet
              </button>
            </div>
          </TabsContent>

          <TabsContent value="broadcasters">
            <div className="bg-[#181818] border border-white/10 rounded-xl overflow-hidden mt-4">
              {apps.length === 0 && <p className="p-4 text-sm text-gray-500">Başvuru yok.</p>}
              {apps.map((a) => (
                <div key={a.id} data-testid={`bc-app-${a.username}`} className="px-4 py-4 border-b border-white/5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium flex items-center gap-1.5">@{a.username}
                        {a.status === "approved" && <span className="text-xs text-[#1d9bf0]">ONAYLI</span>}
                        {a.status === "rejected" && <span className="text-xs text-red-400">REDDEDİLDİ</span>}
                        {a.status === "pending" && <span className="text-xs text-amber-400">BEKLİYOR</span>}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">Kategori: {a.category}</div>
                    </div>
                    {a.status === "pending" && (
                      <div className="flex gap-2 shrink-0">
                        <button data-testid={`bc-approve-${a.username}`} onClick={() => approveApp(a.id)} className="flex items-center gap-1 text-xs bg-[#1d9bf0] hover:bg-[#1a8cd8] text-white px-3 py-1.5 rounded"><Check size={14} /> Onayla</button>
                        <button data-testid={`bc-reject-${a.username}`} onClick={() => rejectApp(a.id)} className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 text-gray-300 px-3 py-1.5 rounded"><X size={14} /> Reddet</button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-400">
                    {a.social_media && <div><span className="text-gray-500">Sosyal medya:</span> {a.social_media}</div>}
                    {a.community && <div><span className="text-gray-500">Topluluk:</span> {a.community}</div>}
                    {a.target && <div className="sm:col-span-2"><span className="text-gray-500">Hedef:</span> {a.target}</div>}
                    {a.contact && <div><span className="text-gray-500">İletişim:</span> {a.contact}</div>}
                    {a.email && <div><span className="text-gray-500">E-posta:</span> {a.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="notifications">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 space-y-4 mt-4">
              <div className="text-white font-medium">Tüm kullanıcılara toplu bildirim gönder</div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Başlık *</label>
                <input data-testid="admin-notif-title" value={notif.title} onChange={(e) => setNotif((n) => ({ ...n, title: e.target.value }))} placeholder="Örn: Yeni özellik yayında!"
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Mesaj</label>
                <textarea data-testid="admin-notif-body" value={notif.body} onChange={(e) => setNotif((n) => ({ ...n, body: e.target.value }))} rows={3} placeholder="Bildirim içeriği"
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914] resize-none" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block">Link (opsiyonel)</label>
                <input data-testid="admin-notif-link" value={notif.link} onChange={(e) => setNotif((n) => ({ ...n, link: e.target.value }))} placeholder="/watch/oda-linki"
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
              </div>
              <button data-testid="admin-notif-send" onClick={sendBroadcast} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Send size={16} /> Gönder
              </button>
            </div>

            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-white font-medium">Telefon Push (Firebase FCM)</div>
                <span className={`text-xs px-2 py-0.5 rounded ${settings?.fcm_service_account ? "bg-green-600/20 text-green-400" : "bg-white/10 text-gray-400"}`}>
                  {settings?.fcm_service_account ? "Yapılandırıldı" : "Yapılandırılmadı"}
                </span>
              </div>
              <p className="text-xs text-gray-500">Firebase Console → Project Settings → Service accounts → "Generate new private key" ile inen JSON'un tamamını yapıştırın. Push bildirimleri kayıtlı cihazlara otomatik gönderilir.</p>
              <textarea data-testid="admin-fcm-json" value={settings?.fcm_service_account || ""} onChange={(e) => set("fcm_service_account", e.target.value)} rows={5}
                placeholder='{ "type": "service_account", "project_id": "...", "private_key": "...", "client_email": "..." }'
                className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 font-mono text-xs focus:outline-none focus:border-[#E50914] resize-none" />
              <button data-testid="admin-save-fcm" onClick={saveSettings} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Save size={16} /> FCM Ayarını Kaydet
              </button>
            </div>
          </TabsContent>

          <TabsContent value="gifts">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-6 mt-4 space-y-4">
              <div className="text-white font-medium flex items-center gap-2"><img src="/ecoin.png" alt="" className="h-6 w-6" /> eCoin Ayarları</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-300 mb-1.5 block">Yayıncı Kazanç Oranı (%)</label>
                  <input data-testid="admin-earn-rate" type="number" value={settings?.coin_earn_rate ?? 40} onChange={(e) => set("coin_earn_rate", parseFloat(e.target.value))}
                    className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
                  <p className="text-xs text-gray-500 mt-1">Örn: 40 → kullanıcı 500 eCoin atınca yayıncı 200 eCoin kazanır. Kalan komisyon sitede kalır.</p>
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1.5 block">Minimum Çekim Tutarı (₺)</label>
                  <input data-testid="admin-min-withdrawal" type="number" value={settings?.min_withdrawal ?? 500} onChange={(e) => set("min_withdrawal", parseFloat(e.target.value))}
                    className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
                </div>
              </div>
              <button data-testid="admin-save-coin-settings" onClick={saveSettings} className="flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-2.5 rounded-md transition">
                <Save size={16} /> Kaydet
              </button>
            </div>

            <div className="bg-[#181818] border border-white/10 rounded-xl overflow-hidden mt-4">
              <div className="px-4 py-3 border-b border-white/10 text-white font-medium">Para Çekme Talepleri</div>
              {withdrawals.length === 0 && <p className="p-4 text-sm text-gray-500">Talep yok.</p>}
              {withdrawals.map((w) => (
                <div key={w.id} data-testid={`withdrawal-${w.id}`} className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium">@{w.username} • <span className="text-[#E50914]">{w.amount}₺</span></div>
                    <div className="text-xs text-gray-400 font-mono break-all">{w.iban}</div>
                    <div className="text-[11px] mt-0.5">
                      {w.status === "pending" && <span className="text-amber-400">BEKLİYOR</span>}
                      {w.status === "paid" && <span className="text-green-400">ÖDENDİ</span>}
                      {w.status === "rejected" && <span className="text-red-400">REDDEDİLDİ</span>}
                    </div>
                  </div>
                  {w.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`withdraw-approve-${w.id}`} onClick={() => approveWithdraw(w.id)} className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded"><Check size={14} /> Ödendi</button>
                      <button data-testid={`withdraw-reject-${w.id}`} onClick={() => rejectWithdraw(w.id)} className="flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 text-gray-300 px-3 py-1.5 rounded"><X size={14} /> Reddet</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-[#181818] border border-white/10 rounded-xl overflow-hidden mt-4">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                  <UserAvatar user={u} premium={u.is_premium} className="h-9 w-9" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm flex items-center gap-1.5">@{u.username} {u.role === "admin" && <span className="text-xs text-[#E50914]">ADMIN</span>} {u.is_premium && <EfsaneBadge />}</div>
                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                  </div>
                  {u.is_premium ? (
                    <button data-testid={`admin-revoke-premium-${u.username}`} onClick={() => revokePremium(u.id)} className="text-xs bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded text-gray-300">Efsane Kaldır</button>
                  ) : (
                    <button data-testid={`admin-grant-premium-${u.username}`} onClick={() => grantPremium(u.id)} className="text-xs bg-gradient-to-r from-[#ff8a00] to-[#E50914] px-2.5 py-1.5 rounded text-white">Efsane Yap</button>
                  )}
                  {u.role !== "admin" && (
                    <button data-testid={`admin-delete-user-${u.username}`} onClick={() => deleteUser(u.id)} className="text-gray-500 hover:text-red-500"><Trash2 size={18} /></button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rooms">
            <div className="bg-[#181818] border border-white/10 rounded-xl overflow-hidden mt-4">
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
                  <div className="flex-1">
                    <div className="text-white text-sm">{r.name}</div>
                    <div className="text-xs text-gray-500">@{r.host?.username} • {r.participant_count} kişi</div>
                  </div>
                  <button data-testid={`admin-delete-room-${r.slug}`} onClick={() => deleteRoom(r.slug)} className="text-gray-500 hover:text-red-500"><Trash2 size={18} /></button>
                </div>
              ))}
              {rooms.length === 0 && <p className="p-4 text-sm text-gray-500">Oda yok.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
